/**
 * Main entry point for the Flexiva application
 * Demonstrates proper OOP architecture with TypeScript
 */

import "./styles.css";
import { Logger } from './services/Logger';

const logger = Logger.scope('App');
import { themeManager } from './services/ThemeManager';
import { FormLoader, getCurrentForm } from './services/FormLoader';
import { SearchManager } from './services/SearchManager';
import { SidebarBuilder } from './services/SidebarBuilder';
import { Application } from './services/Application';
import { configManager } from './services/ConfigManager';
import { GlobalErrorHandler } from './services/GlobalErrorHandler';
import { ToasterService } from './services/ToasterService';
import { UserProfilePanel } from './services/UserProfilePanel';
import { oemConfig } from './services/OEMConfig';
import { PopupWindowService } from './services/PopupWindowService';
import { tenantService } from './services/TenantService';
import { authService } from './services/Authentication';
import { NavigationHistory } from './services/NavigationHistory';
import { LoginForm, AuthUser } from './components/auth';
import { ZIndex } from './constants/ZIndex';
import { userSettingsService, SettingScope } from './services/UserSettingsService';
import { initVersionChecker } from './services/VersionChecker';
import { TaskForm } from './components/TaskForm';
// Import auth forms to ensure they're bundled
import './forms/auth/login';

// Football-specific auth (lazy loaded)
let footballAuthAdapter: any = null;

/**
 * Get Football auth adapter (lazy load for performance)
 */
async function getFootballAuthAdapter() {
  if (!footballAuthAdapter) {
    try {
      const { getFootballAuthAdapter: getAdapter } = await import('./tenants/football/services/FootballAuthAdapter');
      footballAuthAdapter = getAdapter();
    } catch (error) {
      logger.error('Failed to load Football auth adapter:', error);
    }
  }
  return footballAuthAdapter;
}

// Deep link storage key
const DEEP_LINK_KEY = 'flexiva_deep_link';

declare global {
  interface Window {
    OpenForm: (name: string) => Promise<void>;
    OpenCompanyForm: (companyId: number, companyName?: string) => void;
    ApplyTheme: (theme: string) => void;
  }
}

/**
 * Main application controller
 */
class AppController {
  private readonly formLoader: FormLoader;
  private readonly searchManager: SearchManager;
  private readonly sidebarBuilder: SidebarBuilder;
  private readonly userProfilePanel: UserProfilePanel;
  private loginForm: LoginForm | null = null;

  constructor() {
    // Initialize core services (themeManager is a singleton)
    this.formLoader = new FormLoader('main-container');
    this.searchManager = new SearchManager(
      'search-input',
      'search-results',
      (formName) => this.loadForm(formName)
    );
    this.sidebarBuilder = new SidebarBuilder(
      'sidebar-groups',
      themeManager,
      (formName) => this.loadForm(formName)
    );
    this.userProfilePanel = new UserProfilePanel();
  }

  /**
   * Initialize the application
   */
  public async init(): Promise<void> {
    // Initialize global error handler first
    GlobalErrorHandler.init();

    // Initialize tenant service - MUST be done before OEMConfig
    await tenantService.initialize();
    
    // [HELP DEBUG] Check if we're on /help path - redirect to static help page
    // This is a fallback in case server-side redirect (_redirects) doesn't work
    const pathname = window.location.pathname;
    logger.info('[HELP DEBUG] pathname:', pathname, 'href:', window.location.href);
    
    if (pathname === '/help' || pathname === '/help/') {
      // Use tenantService which handles both subdomain (production) and ?tenant= (development)
      const tenant = tenantService.getTenantId();
      logger.info('[HELP DEBUG] Detected /help path, tenant:', tenant);
      
      // Redirect to the tenant-specific static help page
      window.location.href = `/oem/${tenant}/help/index.html`;
      return;
    }
    
    // Check for specification tenant - render standalone document viewer
    if (tenantService.getTenantId() === 'specification') {
      await this.renderSpecificationViewer();
      return;
    }
    
    // Check for postman tenant - redirect to static landing page
    if (tenantService.getTenantId() === 'postman') {
      window.location.href = '/oem/postman/index.html';
      return;
    }

    // Check for cloud-sync tenant - redirect to static landing page
    if (tenantService.getTenantId() === 'cloud-sync') {
      window.location.href = '/oem/cloud-sync/index.html';
      return;
    }

    // Check for tenant error (invalid tenant ID)
    if (tenantService.hasError()) {
      this.showTenantError(tenantService.getError()!);
      return;
    }

    // Initialize popup window service
    PopupWindowService.init();

    // Check if we're running in popup mode
    const isPopup = PopupWindowService.isPopupWindow();
    if (isPopup) {
      await this.initPopupMode();
      return;
    }

    // Load OEM configuration (now tenant-aware)
    await oemConfig.load();

    // Apply OEM branding to HTML
    this.applyOEMBranding();

    // Initialize toaster service
    ToasterService.init();

    // Initialize configuration
    configManager.init();

    // Initialize theme (using shared singleton)
    themeManager.init();

    // Display app version
    Application.displayVersion();

    // Build sidebar
    await this.sidebarBuilder.build();

    // Initialize search with sidebar items
    this.searchManager.setItems(this.sidebarBuilder.getSearchableItems());
    this.searchManager.init();

    // Initialize tenant-specific remote search provider
    await this.initTenantSearchProvider();

    // Initialize sidebar collapse
    Application.initSidebarCollapse();

    // Set initial sidebar state based on screen size
    this.initSidebarState();

    // Initialize settings controls
    this.initSettingsControls();

    // Initialize user profile panel
    this.userProfilePanel.init();

    // Get the appropriate auth service for this tenant
    const currentTenantId = tenantService.getTenantId();
    const authAdapter = currentTenantId === 'football' 
      ? await getFootballAuthAdapter() 
      : authService;
    
    // Register session expired callback
    if (authAdapter) {
      authAdapter.onSessionExpired(() => this.handleSessionExpired());
    }

    // Check if authentication is required
    const requiresAuth = oemConfig.requiresAuthentication();
    let isAuthenticated = authAdapter?.isAuthenticated() ?? false;
    
    // If we have a session, verify it's still valid with the server
    if (isAuthenticated && authAdapter) {
      try {
        logger.debug('Verifying existing session...');
        isAuthenticated = await authAdapter.verifySession();
        if (!isAuthenticated) {
          logger.info('Session expired or invalid');
        } else {
          // Session is valid - update navbar with the user profile
          const user = authAdapter.getCurrentUser();
          if (user) {
            logger.debug('Updating navbar for returning user:', user.name);
            this.updateNavbarUserProfile(user);
          }
        }
      } catch (error) {
        // Session verification failed (network error, server down, etc.)
        logger.error('Session verification failed:', error);
        isAuthenticated = false;
      }
    }
    
    logger.info(`Auth check: tenant=${currentTenantId}, requiresAuth=${requiresAuth}, isAuthenticated=${isAuthenticated}`);
    
    if (requiresAuth && !isAuthenticated) {
      // Store deep link if user was trying to access a specific form
      const requestedForm = window.location.hash ? window.location.hash.substring(1) : null;
      if (requestedForm && requestedForm !== 'login') {
        userSettingsService.set(DEEP_LINK_KEY, requestedForm, SettingScope.SESSION);
        logger.debug('Stored deep link:', requestedForm);
      }

      // Show login screen
      await this.showLoginScreen();
      return;
    }

    // Load initial form - EXPLICIT call, not automatic in constructor
    // This loads from URL hash (e.g., #entity) or falls back to dashboard
    await this.loadInitialForm();

    // Hide the loading splash now that app is ready
    this.hideLoadingSplash();

    // Start version checking for automatic update detection
    initVersionChecker();

    // Expose global APIs for backward compatibility
    this.exposeGlobalAPIs();

    // Wire up sign-out handler
    this.setupSignOutHandler();
  }

  /**
   * Load the initial form after authentication (if required)
   */
  private async loadInitialForm(): Promise<void> {
    // Check for stored deep link first
    const storedDeepLink = userSettingsService.get<string>(DEEP_LINK_KEY);
    if (storedDeepLink) {
      userSettingsService.remove(DEEP_LINK_KEY, SettingScope.SESSION);
      logger.debug('Restoring deep link:', storedDeepLink);
      await this.loadForm(storedDeepLink);
      return;
    }

    // Check URL hash
    const hasUrlHash = window.location.hash && window.location.hash.length > 1;
    if (hasUrlHash) {
      this.formLoader.loadInitialForm();
    } else {
      // Load the tenant's configured start screen
      const startScreen = oemConfig.getStartScreen() || 'dashboard';
      await this.loadForm(startScreen);
    }
  }

  /**
   * Show the login screen overlay
   */
  private async showLoginScreen(): Promise<void> {
    logger.info('Showing login screen (authentication required)');

    // Disable exit warning while on login screen
    NavigationHistory.setExitWarningEnabled(false);

    // Clean up any existing login form first
    if (this.loginForm) {
      this.loginForm.destroy();
      this.loginForm = null;
    }
    
    // Remove any existing login overlay
    const existingOverlay = document.getElementById('login-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }

    // Hide sidebar and navbar
    this.hideAppChrome();

    // Create login overlay container
    const overlay = document.createElement('div');
    overlay.id = 'login-overlay';
    overlay.className = 'fixed inset-0 bg-base-100';
    overlay.style.zIndex = String(ZIndex.OVERLAY);
    document.body.appendChild(overlay);

    // Get auth service - use Football adapter for football tenant
    const tenantId = tenantService.getTenantId();
    let customAuthService = undefined;
    
    if (tenantId === 'football') {
      logger.info('Using Football auth adapter');
      customAuthService = await getFootballAuthAdapter();
    }

    // Create LoginForm component
    this.loginForm = new LoginForm(overlay, {
      onSuccess: async (user: AuthUser, rememberMe: boolean) => {
        logger.info('Login successful:', user.email, 'Remember:', rememberMe);
        await this.handleLoginSuccess(user);
      },
      onForgotPassword: () => {
        // Open forgot password in new window/modal
        // For now, just show a toast
        ToasterService.info('Password reset functionality coming soon');
      },
      onSignup: () => {
        // Open signup in new window/modal
        // For now, just show a toast
        ToasterService.info('Account registration coming soon');
      },
      showRememberMe: true,
      showSocialLogin: true,
      showThemeToggle: true,
      authService: customAuthService
    });

    await this.loginForm.initialize();

    // Hide the loading splash now that login screen is ready
    this.hideLoadingSplash();
  }

  /**
   * Handle successful login
   */
  private async handleLoginSuccess(user: AuthUser): Promise<void> {
    // Re-enable exit warning now that we are entering the app
    NavigationHistory.setExitWarningEnabled(true);

    // Destroy login form
    this.loginForm?.destroy();
    this.loginForm = null;

    // Remove login overlay
    const overlay = document.getElementById('login-overlay');
    overlay?.remove();

    // Update navbar with user profile
    this.updateNavbarUserProfile(user);

    // Show sidebar and navbar
    this.showAppChrome();

    // Load initial form (may restore deep link)
    await this.loadInitialForm();

    // Start version checking for automatic update detection
    initVersionChecker();

    // Expose global APIs (if not already)
    this.exposeGlobalAPIs();

    // Wire up sign-out handler
    this.setupSignOutHandler();
  }

  /**
   * Update the navbar user profile with authenticated user data
   */
  private updateNavbarUserProfile(user: AuthUser): void {
    logger.info('Updating navbar with user profile:', {
      name: user.name,
      jobTitle: user.jobTitle,
      avatar: user.avatar,
      email: user.email
    });

    // Get display name for navbar (truncated if too long)
    // User requested full name (e.g. "Josie Musto") but truncated if very long
    let displayName = user.name || user.email?.split('@')[0] || 'User';
    const MAX_LEN = 18;
    if (displayName.length > MAX_LEN) {
      displayName = displayName.substring(0, MAX_LEN - 3) + '...';
    }
    
    // Navbar (compact)
    const navbarAvatar = document.getElementById('navbar-user-avatar') as HTMLImageElement;
    const navbarName = document.getElementById('navbar-user-name');
    const navbarTitle = document.getElementById('navbar-user-title');
    
    // Always update all fields - use empty string if no value (clears previous user's data)
    if (navbarAvatar) {
      navbarAvatar.src = user.avatar || 'https://api.trisys.co.uk/Chat/Profiles/dall-e.png';
    }
    if (navbarName) {
      navbarName.textContent = displayName;
    }
    if (navbarTitle) {
      navbarTitle.textContent = user.jobTitle || '';
    }
    
    // Dropdown (full)
    const dropdownAvatar = document.getElementById('dropdown-user-avatar') as HTMLImageElement;
    const dropdownName = document.getElementById('dropdown-user-name');
    const dropdownTitle = document.getElementById('dropdown-user-title');
    const dropdownCompany = document.getElementById('dropdown-user-company');
    
    // Always update all fields - use empty string if no value (clears previous user's data)
    if (dropdownAvatar) {
      dropdownAvatar.src = user.avatar || 'https://api.trisys.co.uk/Chat/Profiles/dall-e.png';
    }
    if (dropdownName) {
      dropdownName.textContent = user.name || displayName;
    }
    if (dropdownTitle) {
      dropdownTitle.textContent = user.jobTitle || '';
    }
    if (dropdownCompany) {
      dropdownCompany.textContent = user.companyName || '';
    }
    
    // Make profile header clickable to open contact form
    if (user.contactId) {
      const profileElements = [dropdownAvatar, dropdownName, dropdownTitle, dropdownCompany];
      const profileHeader = dropdownAvatar?.closest('.flex.flex-col.items-center');
      
      if (profileHeader) {
        profileHeader.classList.add('cursor-pointer', 'hover:opacity-80', 'transition-opacity');
        profileHeader.setAttribute('title', 'View your contact record');
        
        // Remove existing listener if any (to avoid duplicates on re-login)
        const existingHandler = (profileHeader as HTMLElement & { _profileClickHandler?: () => void })._profileClickHandler;
        if (existingHandler) {
          profileHeader.removeEventListener('click', existingHandler);
        }
        
        // Add click handler to open contact form
        const clickHandler = () => {
          logger.debug('Profile header clicked, opening contact:', user.contactId);
          
          // Close the dropdown first
          const activeElement = document.activeElement as HTMLElement;
          if (activeElement) {
            activeElement.blur();
          }
          
          // Navigate to contact detail form with contactId as query param
          this.loadForm(`contactDetail?id=${user.contactId}`);
        };
        
        (profileHeader as HTMLElement & { _profileClickHandler?: () => void })._profileClickHandler = clickHandler;
        profileHeader.addEventListener('click', clickHandler);
      }
    }
    
    logger.debug('Navbar user profile updated:', displayName);
  }

  /**
   * Handle session expired - redirect to login screen
   */
  private async handleSessionExpired(): Promise<void> {
    logger.warn('Session expired, redirecting to login');
    ToasterService.warning('Your session has expired. Please log in again.');
    
    // Store current form as deep link
    const currentForm = window.location.hash ? window.location.hash.substring(1) : null;
    if (currentForm && currentForm !== 'login') {
      await userSettingsService.set(DEEP_LINK_KEY, currentForm, SettingScope.SESSION);
    }

    // Show login screen
    await this.showLoginScreen();
  }

  /**
   * Hide the app chrome (sidebar, navbar) for login screen
   */
  private hideAppChrome(): void {
    // Hide sidebar
    const drawerSide = document.querySelector('.drawer-side') as HTMLElement;
    if (drawerSide) {
      drawerSide.style.display = 'none';
    }

    // Hide navbar
    const navbar = document.querySelector('.navbar') as HTMLElement;
    if (navbar) {
      navbar.style.display = 'none';
    }

    // Hide main content area
    const mainContainer = document.getElementById('main-container');
    if (mainContainer) {
      mainContainer.style.display = 'none';
    }

    // Add login-mode class to body
    document.body.classList.add('login-mode');
  }

  /**
   * Show the app chrome (sidebar, navbar) after login
   */
  private showAppChrome(): void {
    // Show sidebar
    const drawerSide = document.querySelector('.drawer-side') as HTMLElement;
    if (drawerSide) {
      drawerSide.style.display = '';
    }

    // Show navbar
    const navbar = document.querySelector('.navbar') as HTMLElement;
    if (navbar) {
      navbar.style.display = '';
    }

    // Show main content area
    const mainContainer = document.getElementById('main-container');
    if (mainContainer) {
      mainContainer.style.display = '';
    }

    // Remove login-mode class from body
    document.body.classList.remove('login-mode');

    // Reset sidebar state
    this.initSidebarState();
  }

  /**
   * Hide the loading splash screen
   */
  private hideLoadingSplash(): void {
    const splash = document.getElementById('loading-splash');
    if (splash) {
      splash.dataset.hidden = 'true';  // Signal to stop the timer
      splash.style.display = 'none';
    }
  }

  /**
   * Setup sign-out handler to return to login screen
   */
  private setupSignOutHandler(): void {
    // Listen for sign-out events
    window.addEventListener('flexiva:signout', async () => {
      logger.info('Sign-out event received');
      
      // Clear authentication using the correct auth service for this tenant
      const tid = tenantService.getTenantId();
      if (tid === 'football') {
        const adapter = await getFootballAuthAdapter();
        if (adapter) {
          await adapter.logout();
        }
      } else {
        await authService.logout();
      }

      // Reset navigation history to prevent "Leaving Application" warning on back button
      NavigationHistory.reset();

      // If authentication is required, show login screen
      // Otherwise, reload the page to reset the app state
      if (oemConfig.requiresAuthentication()) {
        await this.showLoginScreen();
      } else {
        // For development/demo mode without authentication, just reload the page
        logger.info('Reloading page after sign-out (no auth required)');
        window.location.reload();
      }
    });
  }

  /**
   * Apply OEM branding to HTML elements
   */
  private applyOEMBranding(): void {
    const branding = oemConfig.getBranding();
    
    // Update page title
    document.title = branding.fullName;
    
    // Update favicon
    const favicon = document.getElementById('favicon') as HTMLLinkElement;
    if (favicon) {
      favicon.href = branding.favicon;
    }
    
    // Update logo icon and its tooltip
    const logoImg = document.querySelector('.navbar img') as HTMLImageElement;
    if (logoImg) {
      logoImg.src = branding.logo.icon;
      logoImg.alt = branding.caption;
      
      // Update tooltip with branding.fullName
      const logoTooltip = logoImg.closest('.tooltip') as HTMLElement;
      if (logoTooltip) {
        logoTooltip.setAttribute('data-tip', branding.fullName);
      }
    }
    
    // Update splash image in user dropdown
    const splashImg = document.querySelector('a[href*="flexiva.co.uk"] img') as HTMLImageElement;
    if (splashImg) {
      splashImg.src = branding.logo.splash;
    }
  }

  /**
   * Show tenant error page when invalid tenant is specified
   */
  private showTenantError(invalidTenantId: string): void {
    // Hide the normal app UI
    const drawer = document.getElementById('app-drawer');
    if (drawer) {
      drawer.parentElement!.style.display = 'none';
    }

    // Only show valid tenants hint on localhost (don't expose customer list publicly)
    const isLocalhost = tenantService.isDevelopment();
    const devHint = isLocalhost 
      ? `<p class="text-sm text-base-content/50 mt-4">Valid tenants: ${tenantService.getValidTenants().join(', ')}</p>`
      : '';

    // Show error page
    const errorHtml = `
      <div class="hero min-h-screen bg-base-200">
        <div class="hero-content text-center">
          <div class="max-w-md">
            <div class="text-6xl mb-4">⚠️</div>
            <h1 class="text-4xl font-bold text-error mb-4">Configuration Error</h1>
            <p class="text-lg mb-2">
              Tenant "<strong class="text-primary">${invalidTenantId}</strong>" not found.
            </p>
            <p class="text-base-content/70 mb-6">
              The requested tenant configuration does not exist. Please check the URL and try again.
            </p>
            <div class="flex flex-col gap-2">
              <a href="/" class="btn btn-primary">
                <i class="fas fa-home mr-2"></i>
                Go to Default
              </a>
              ${devHint}
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.innerHTML = errorHtml;
    document.title = 'Configuration Error - Tenant Not Found';
    logger.error(`Invalid tenant requested: ${invalidTenantId}`);
  }

  /**
   * Render standalone specification viewer (no app chrome)
   * This bypasses all app infrastructure for a clean document reading experience
   */
  private async renderSpecificationViewer(): Promise<void> {
    // Import marked dynamically
    const { marked } = await import('marked');

    // Specification slug to file path mapping
    const SPEC_MAPPING: Record<string, { path: string; title: string }> = {
      'postman-flexiva': {
        path: '/specifications/postman/postman-flexiva.co.uk.md',
        title: 'Postman to Flexiva App Generator'
      },
      'swagger-to-app': {
        path: '/specifications/Swagger-To-App-Generator.md',
        title: 'Swagger-To-App Generator'
      },
      'cv-ripper': {
        path: '/specifications/CV-Ripper.md',
        title: 'CV Ripper — Product & Technical Specification'
      }
      // Add more specs as needed
    };

    // Get document slug from URL hash (e.g., #postman-flexiva or ?doc=postman-flexiva)
    const hash = window.location.hash.substring(1); // Remove #
    const urlParams = new URLSearchParams(window.location.search);
    const docSlug = hash || urlParams.get('doc') || null;

    // Hide loading splash
    const splash = document.getElementById('loading-splash');
    if (splash) splash.classList.add('hidden');

    // Build the viewer HTML - complete document replacement
    const buildViewerHTML = (content: string, title: string) => {
      // Completely replace the document to bypass all app styles
      document.open();
      document.write(`
<!DOCTYPE html>
<html lang="en" data-theme="light">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css">
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github.min.css">
  <script src="https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/highlight.min.js"></script>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; line-height: 1.7; }
    h1 { font-size: 2.25rem !important; font-weight: 700 !important; margin-top: 1.5rem !important; margin-bottom: 3rem !important; padding-bottom: 1.5rem !important; border-bottom: 2px solid #e5e7eb; }
    h2 { font-size: 1.75rem !important; font-weight: 700 !important; margin-top: 2.5rem !important; margin-bottom: 1rem !important; padding-top: 0.5rem; }
    h3 { font-size: 1.4rem !important; font-weight: 600 !important; margin-top: 2rem !important; margin-bottom: 0.75rem !important; padding-top: 0.5rem; }
    h4 { font-size: 1.2rem !important; font-weight: 600 !important; margin-top: 1.5rem !important; margin-bottom: 0.5rem !important; padding-top: 0.25rem; }
    h5 { font-size: 1.1rem !important; font-weight: 600 !important; margin-top: 1.25rem !important; margin-bottom: 0.5rem !important; }
    h6 { font-size: 1rem !important; font-weight: 600 !important; margin-top: 1rem !important; margin-bottom: 0.5rem !important; }
    p { margin: 1.25rem 0 !important; }
    .logo-header { display: flex; align-items: center; gap: 0.75rem; padding: 1rem 0; border-bottom: 1px solid #e5e7eb; margin-bottom: 1rem; }
    .logo-header img { height: 2.5rem; }
    .logo-header span { font-size: 1.25rem; font-weight: 600; color: #374151; }
    ul, ol { margin: 1.25rem 0; padding-left: 1.5rem; }
    li { margin: 0.5rem 0; }
    a { color: #3b82f6; text-decoration: none; }
    a:hover { text-decoration: underline; }
    code { background: #f3f4f6; padding: 0.2rem 0.4rem; border-radius: 0.25rem; font-size: 0.9em; font-family: 'Consolas', 'Monaco', 'Courier New', monospace !important; }
    pre { background: #f3f4f6; padding: 1rem; border-radius: 0.5rem; overflow-x: auto; margin: 1.5rem 0; font-family: 'Consolas', 'Monaco', 'Courier New', monospace !important; font-size: 0.875rem; line-height: 1.5; }
    pre code { background: none; padding: 0; font-family: inherit !important; }
    blockquote { border-left: 4px solid #3b82f6; background: #f9fafb; padding: 1rem 1.5rem; margin: 1.5rem 0; border-radius: 0 0.5rem 0.5rem 0; }
    table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; }
    th { background: #f3f4f6; padding: 0.75rem; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
    td { padding: 0.75rem; border-bottom: 1px solid #e5e7eb; }
    hr { margin: 3rem 0; border: none; border-top: 1px solid #e5e7eb; }
    strong { font-weight: 600; }
    .bg-white { background-color: #fff; }
    .text-gray-900 { color: #111827; }
    .text-gray-500 { color: #6b7280; }
    .max-w-4xl { max-width: 56rem; }
    .mx-auto { margin-left: auto; margin-right: auto; }
    .px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
    .py-8 { padding-top: 2rem; padding-bottom: 2rem; }
    .mt-16 { margin-top: 4rem; }
    .pt-8 { padding-top: 2rem; }
    .border-t { border-top: 1px solid #e5e7eb; }
    .text-center { text-align: center; }
    .text-sm { font-size: 0.875rem; }
    @media print {
      body { font-size: 11pt; }
      pre { white-space: pre-wrap; word-wrap: break-word; }
    }
  </style>
</head>
<body class="bg-white text-gray-900">
  <div class="max-w-4xl mx-auto px-6 py-8">
    <div class="logo-header">
      <img src="https://app.flexiva.co.uk/images/flexiva/Flexiva-July-2025-square-icon.png" alt="Flexiva">
      <span>Flexiva Specification</span>
    </div>
    ${content}
    <footer class="mt-16 pt-8 border-t text-center text-gray-500 text-sm">
      <a href="https://www.business-software.group" target="_blank" rel="noopener noreferrer">
        <img src="https://lirp.cdn-website.com/b9b0da0b/dms3rep/multi/opt/Business-Software-Group-1920w.png" alt="Business Software Group" style="height: 40px; margin: 0 auto 0.75rem auto;">
      </a>
      <p>© 1994 - 2026 <a href="https://www.business-software.group" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">Business Software Group</a></p>
    </footer>
  </div>
  <script>hljs.highlightAll();</script>
</body>
</html>
      `);
      document.close();
    };

    const buildIndexHTML = () => {
      let listHtml = '<ul>';

      /* 11 Jan 2026: We do not want a list of all documents
      for (const [slug, spec] of Object.entries(SPEC_MAPPING)) {
        // Use onclick with location.href to force full page reload
        listHtml += `<li><a href="${window.location.origin}${window.location.pathname}#${slug}" onclick="window.location.href=this.href; return false;">${spec.title}</a></li>`;
      }
      listHtml += '</ul>';
      */

      // 11 Jan 2026: We do not want a list of all documents
      listHtml = '';

      buildViewerHTML(`
        <p>You are only able to view documents from a full URL supplied by us.</p>
        ${listHtml}
      `, 'Flexiva Specifications');
    };

    const buildErrorHTML = (slug: string) => buildViewerHTML(`
      <div style="text-align: center; padding: 3rem 0;">
        <div style="font-size: 4rem; margin-bottom: 1.5rem;">📄</div>
        <h1 style="border: none;">Document Not Found</h1>
        <p>The specification "<strong>${slug}</strong>" could not be found.</p>
        <p><a href="#" class="btn">View Available Documents</a></p>
      </div>
    `, 'Document Not Found');

    // No doc specified - show index
    if (!docSlug) {
      buildIndexHTML();
      return;
    }

    // Check if doc exists
    const spec = SPEC_MAPPING[docSlug];
    if (!spec) {
      buildErrorHTML(docSlug);
      return;
    }

    // Fetch and render the markdown
    try {
      const response = await fetch(spec.path);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const markdown = await response.text();
      marked.setOptions({ gfm: true, breaks: false });
      const html = await marked(markdown);
      
      buildViewerHTML(html, spec.title);

      // Add copy buttons to code blocks
    } catch (error) {
      logger.error('Failed to load specification:', error);
      buildErrorHTML(docSlug);
    }
  }

  /**
   * Initialize tenant-specific remote search provider
   * Each tenant can have its own API-based search
   */
  private async initTenantSearchProvider(): Promise<void> {
    const tenantId = tenantService.getTenantId();
    
    if (tenantId === 'football') {
      // Dynamically import football search service
      try {
        const { searchFootball } = await import('./tenants/football/services/SearchService');
        this.searchManager.setRemoteSearchProvider(searchFootball);
        logger.info('Football search provider registered');
      } catch (error) {
        logger.error('Failed to load football search provider:', error);
      }
    } else if (tenantId === 'trisys-2026-01-13') {
      // Dynamically import TriSys search service
      try {
        const { searchGlobal } = await import('./tenants/trisys-2026-01-13/services/SearchService');
        this.searchManager.setRemoteSearchProvider(searchGlobal);
        logger.info('TriSys search provider registered');
      } catch (error) {
        logger.error('Failed to load TriSys search provider:', error);
      }
    }
    // Add other tenant search providers here as needed
  }

  /**
   * Initialize sidebar state based on screen size
   */
  private initSidebarState(): void {
    const drawer = document.getElementById('app-drawer') as HTMLInputElement;
    const searchContainer = document.querySelector('.search-box-container') as HTMLElement;
    
    if (drawer) {
      // Open sidebar on desktop (screen width >= 640px), closed on mobile
      drawer.checked = window.innerWidth >= 640;
      
      // Search box position is now handled by CSS and stays fixed
    }
  }

  /**
   * Initialize settings controls
   */
  private initSettingsControls(): void {
    // Settings button
    const settingsBtn = document.getElementById('btn-settings');
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => {
        this.loadForm('settings');
      });
    }

    // Help button
    const helpButton = document.getElementById('help-button');
    if (helpButton) {
      helpButton.addEventListener('click', () => {
        window.open(oemConfig.getBranding().documentation, '_blank');
      });
    }

    // Add New Record dropdown
    this.initAddRecordDropdown();
  }

  /**
   * Initialize the "+ Add" dropdown for creating new records
   */
  private initAddRecordDropdown(): void {
    const dropdown = document.getElementById('add-record-dropdown');
    if (!dropdown) return;

    // Handle clicks on dropdown items
    dropdown.addEventListener('click', async (event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('[data-add-entity]') as HTMLElement;
      
      if (!link) return;
      
      event.preventDefault();
      const entityType = link.dataset.addEntity;
      
      // Close the dropdown by blurring the trigger
      (document.activeElement as HTMLElement)?.blur();
      
      logger.debug('Add new record clicked', { entityType });
      
      // Map entity types to form names with id=0 for CREATE mode
      switch (entityType) {
        case 'Contact':
          await this.loadForm('contactDetail?id=0');
          break;
        case 'Company':
          await this.loadForm('companyDetail');
          break;
        case 'CV':
          ToasterService.info('CV/Resume form coming soon');
          break;
        case 'Requirement':
          ToasterService.info('Requirement form coming soon');
          break;
        case 'Placement':
          ToasterService.info('Placement form coming soon');
          break;
        case 'Timesheet':
          ToasterService.info('Timesheet form coming soon');
          break;
        case 'Note':
          this.handleAddNoteOrTask(false);
          break;
        case 'Task':
          this.handleAddNoteOrTask(true);
          break;
        case 'PostIt':
          ToasterService.info('Post-It Note form coming soon');
          break;
        default:
          ToasterService.warning(`Unknown entity type: ${entityType}`);
      }
    });
  }

  /**
   * Handle adding a new Note or Scheduled Task from the toolbar
   * Detects if a contact form is open and pre-populates contact
   * Current user is always set as the default owner
   */
  private handleAddNoteOrTask(scheduled: boolean): void {
    // Get the current form to check if it's a contact form
    const currentForm = getCurrentForm();
    
    // Get contact ID and name from the current form (if applicable)
    const defaultContactId = currentForm?.getActiveContactId() ?? undefined;
    const defaultContactName = currentForm?.getActiveContactName() ?? undefined;
    
    logger.debug('Opening TaskForm from toolbar', { 
      scheduled, 
      hasActiveContact: !!defaultContactId,
      defaultContactId,
      defaultContactName
    });
    
    // Open TaskForm with defaults
    // Note: Current user is automatically set as owner in TaskForm.createNewTask()
    TaskForm.show({
      scheduled,
      defaultContactId,
      defaultContactName
    });
  }

  /**
   * Load a form by name
   */
  private async loadForm(formName: string): Promise<void> {
    try {
      await this.formLoader.load(formName);
    } catch (error) {
      logger.error(`Failed to load form ${formName}:`, error);
      // Don't propagate the error - keep the app functional
    }
  }

  /**
   * Expose APIs to window for backward compatibility
   */
  private exposeGlobalAPIs(): void {
    window.OpenForm = async (name: string) => {
      await this.loadForm(name);
    };
    
    // Central navigation to company form
    window.OpenCompanyForm = (companyId: number, companyName?: string) => {
      logger.info('OpenCompanyForm called', { companyId, companyName });
      this.loadForm(`companyDetail?id=${companyId}`);
    };
    
    window.ApplyTheme = (theme: string) => themeManager.apply(theme);
  }

  /**
   * Initialize popup mode - simplified UI without sidebar/navbar
   */
  private async initPopupMode(): Promise<void> {
    logger.info('Initializing in popup mode');

    // Load OEM configuration
    await oemConfig.load();

    // Apply OEM branding
    this.applyOEMBranding();

    // Initialize toaster service
    ToasterService.init();

    // Initialize configuration
    configManager.init();

    // Initialize theme
    themeManager.init();

    // Hide sidebar and navbar for popup mode
    this.setupPopupLayout();

    // Get the form to load from URL
    const urlParams = new URLSearchParams(window.location.search);
    const formName = urlParams.get('form') || 'entity';

    // Load the form
    await this.loadForm(formName);

    // Hide the loading splash now that popup is ready
    this.hideLoadingSplash();

    // Request form state from parent window
    PopupWindowService.requestFormState();

    // Expose global APIs
    this.exposeGlobalAPIs();
  }

  /**
   * Setup layout for popup mode - hide sidebar/navbar, maximize form area
   */
  private setupPopupLayout(): void {
    // Hide the drawer sidebar
    const drawer = document.getElementById('app-drawer') as HTMLInputElement;
    if (drawer) {
      drawer.checked = false;
    }

    // Hide the sidebar completely
    const sidebar = document.querySelector('.drawer-side') as HTMLElement;
    if (sidebar) {
      sidebar.style.display = 'none';
    }

    // Hide the navbar
    const navbar = document.querySelector('.navbar') as HTMLElement;
    if (navbar) {
      navbar.style.display = 'none';
    }

    // Adjust main content to take full width
    const mainContent = document.querySelector('.drawer-content') as HTMLElement;
    if (mainContent) {
      mainContent.classList.remove('lg:ml-64');
      mainContent.style.marginLeft = '0';
    }

    // Add popup-mode class to body for CSS targeting
    document.body.classList.add('popup-mode');

    // Update page title to indicate popup
    const urlParams = new URLSearchParams(window.location.search);
    const formName = urlParams.get('form') || 'Form';
    document.title = `${oemConfig.getBrandName()} - ${formName.charAt(0).toUpperCase() + formName.slice(1)}`;
  }
}

// Bootstrap the application
const app = new AppController();
app.init().catch(error => {
  logger.error('Failed to initialize application:', error);
  
  // Ensure splash is hidden even on error - show error to user
  const splash = document.getElementById('loading-splash');
  if (splash) {
    splash.dataset.hidden = 'true';
    splash.style.display = 'none';
  }
  if (window._splashInterval) {
    clearInterval(window._splashInterval);
  }
  
  // Show error message to user
  document.body.innerHTML = `
    <div class="min-h-screen flex items-center justify-center bg-base-100">
      <div class="card bg-error/10 border border-error max-w-lg">
        <div class="card-body text-center">
          <h2 class="text-2xl font-bold text-error mb-2">
            <i class="fas fa-exclamation-triangle mr-2"></i>
            Application Error
          </h2>
          <p class="text-base-content/70 mb-4">
            Failed to initialize the application. Please try refreshing the page.
          </p>
          <p class="text-sm text-base-content/50 font-mono bg-base-200 p-2 rounded">
            ${error instanceof Error ? error.message : String(error)}
          </p>
          <div class="mt-4">
            <button onclick="location.reload()" class="btn btn-primary">
              <i class="fas fa-refresh mr-2"></i>Refresh Page
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
});
